import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, Zap, Users, HeadphonesIcon, BarChart3, Clock, CheckCircle2, ArrowRight, Star } from 'lucide-react';
import VideoModal from '../../components/VideoModal';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const features = [
    {
      icon: Shield,
      title: 'Remote Monitoring',
      description: 'Real-time monitoring of all your endpoints with automated alerts and health scoring',
      color: 'text-blue-600'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Automate routine tasks, patch management, and system maintenance across your fleet',
      color: 'text-purple-600'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Comprehensive CRM tools to manage clients, contacts, and service agreements',
      color: 'text-green-600'
    },
    {
      icon: HeadphonesIcon,
      title: 'Help Desk',
      description: 'Integrated ticketing system with automated workflows and client portal access',
      color: 'text-orange-600'
    },
    {
      icon: BarChart3,
      title: 'PSA Integration',
      description: 'Professional Services Automation with time tracking, billing, and project management',
      color: 'text-cyan-600'
    },
    {
      icon: Clock,
      title: 'Real-Time Insights',
      description: 'Advanced analytics and reporting to make data-driven decisions for your MSP',
      color: 'text-pink-600'
    }
  ];

  const benefits = [
    'Reduce downtime with proactive monitoring',
    'Save 40% on operational costs',
    'Scale your MSP effortlessly',
    'Increase customer satisfaction',
    'Automate repetitive tasks',
    'Centralize all tools in one platform'
  ];

  const testimonials = [
    {
      name: 'John Smith',
      company: 'TechCare Solutions',
      text: 'This RMM platform transformed how we manage our clients. The automation features alone saved us 20 hours per week.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      company: 'CloudMSP Inc',
      text: 'Best decision we made for our business. The integrated PSA and help desk are game-changers.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      company: 'SecureIT Partners',
      text: 'Exceptional monitoring capabilities and the customer support is top-notch. Highly recommended!',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <button onClick={() => navigate('/public/about')} className="text-slate-600 hover:text-slate-900 transition-colors">About</button>
              <button onClick={() => navigate('/public/resources')} className="text-slate-600 hover:text-slate-900 transition-colors">Resources</button>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')} className="text-sm sm:text-base">
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-sm sm:text-base">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
              Complete RMM, PSA & CRM
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                All-in-One Platform
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Manage, monitor, and grow your MSP business with our powerful unified platform. 
              Remote monitoring, help desk, PSA, and CRM in one solution.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8"
              >
                Start 14-Day Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => setShowVideoModal(true)}
                className="text-lg px-8"
              >
                Watch Demo
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-4">No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-blue-600">10K+</div>
              <div className="text-slate-600 mt-1">Active Endpoints</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-purple-600">500+</div>
              <div className="text-slate-600 mt-1">MSP Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-green-600">99.9%</div>
              <div className="text-slate-600 mt-1">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-600">24/7</div>
              <div className="text-slate-600 mt-1">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Everything You Need to Run Your MSP</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful features designed specifically for managed service providers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <feature.icon className={`w-12 h-12 ${feature.color} mb-4`} />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/public/features')}
              variant="outline"
              className="text-lg"
            >
              View All Features
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why MSPs Choose Our Platform
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')}
                className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Get Started Today
              </Button>
            </div>
            <div className="relative">
              <div className="rounded-lg shadow-2xl overflow-hidden border border-slate-200">
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-4 flex items-center space-x-2">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-white text-sm ml-4">RMM Dashboard</span>
                </div>
                <div className="bg-white p-6">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="text-2xl font-bold text-blue-600">247</div>
                      <div className="text-xs text-slate-600 mt-1">Active Endpoints</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="text-2xl font-bold text-green-600">98%</div>
                      <div className="text-xs text-slate-600 mt-1">Health Score</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">12</div>
                      <div className="text-xs text-slate-600 mt-1">Open Tickets</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Server-01</span>
                      </div>
                      <span className="text-xs text-slate-500">CPU: 45%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Workstation-15</span>
                      </div>
                      <span className="text-xs text-slate-500">CPU: 23%</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium">Laptop-42</span>
                      </div>
                      <span className="text-xs text-slate-500">CPU: 87%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Screenshots Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">See It In Action</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get a glimpse of our powerful platform designed for MSPs
            </p>
          </div>

          <div className="space-y-24">
            {/* Asset Management Screenshot */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-lg shadow-xl overflow-hidden border border-slate-200">
                  <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-3 flex items-center space-x-2">
                    <div className="flex space-x-1.5">
                      <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                    </div>
                    <span className="text-white text-xs ml-3">Asset Management</span>
                  </div>
                  <div className="bg-white p-6">
                    <div className="mb-4">
                      <div className="bg-slate-100 h-8 rounded mb-3 w-2/3"></div>
                      <div className="flex gap-2 mb-4">
                        <div className="bg-blue-100 h-6 rounded w-20"></div>
                        <div className="bg-green-100 h-6 rounded w-24"></div>
                        <div className="bg-purple-100 h-6 rounded w-16"></div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="bg-slate-200 h-4 rounded w-32"></div>
                            <div className="flex gap-2">
                              <div className="w-12 h-6 bg-green-100 rounded"></div>
                              <div className="w-12 h-6 bg-blue-100 rounded"></div>
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-3 mt-3">
                            <div>
                              <div className="bg-slate-100 h-3 rounded w-full mb-1"></div>
                              <div className="bg-slate-200 h-3 rounded w-3/4"></div>
                            </div>
                            <div>
                              <div className="bg-slate-100 h-3 rounded w-full mb-1"></div>
                              <div className="bg-slate-200 h-3 rounded w-3/4"></div>
                            </div>
                            <div>
                              <div className="bg-slate-100 h-3 rounded w-full mb-1"></div>
                              <div className="bg-slate-200 h-3 rounded w-3/4"></div>
                            </div>
                            <div>
                              <div className="bg-slate-100 h-3 rounded w-full mb-1"></div>
                              <div className="bg-slate-200 h-3 rounded w-3/4"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Centralized Asset Management</h3>
                <p className="text-lg text-slate-600 mb-6">
                  Monitor all your endpoints from a single dashboard. View real-time health metrics, 
                  system information, and security scores at a glance.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Real-time CPU, memory, and disk monitoring</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Automated health and security scoring</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">One-click remote access integration</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Monitoring Screenshot */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Advanced Performance Monitoring</h3>
                <p className="text-lg text-slate-600 mb-6">
                  Track system performance with detailed metrics and historical data. 
                  Get alerted before issues become problems.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Custom alert thresholds and notifications</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Historical performance graphs</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Application and service monitoring</span>
                  </li>
                </ul>
              </div>
              <div>
                <div className="rounded-lg shadow-xl overflow-hidden border border-slate-200">
                  <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-3 flex items-center space-x-2">
                    <div className="flex space-x-1.5">
                      <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                      <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                    </div>
                    <span className="text-white text-xs ml-3">Performance Metrics</span>
                  </div>
                  <div className="bg-white p-6">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                        <div className="text-xs text-slate-600 mb-1">CPU Usage</div>
                        <div className="text-3xl font-bold text-blue-600">45%</div>
                        <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full" style={{width: '45%'}}></div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                        <div className="text-xs text-slate-600 mb-1">Memory Usage</div>
                        <div className="text-3xl font-bold text-purple-600">67%</div>
                        <div className="mt-2 h-2 bg-purple-200 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-600 rounded-full\" style={{width: '67%'}}></div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-slate-700 mb-3">24-Hour Performance</div>
                      <div className="flex items-end justify-between h-32 gap-1">
                        {[40, 45, 42, 48, 52, 49, 51, 47, 45, 43, 46, 50, 48, 52, 55, 53, 51, 49, 47, 45].map((height, i) => (
                          <div key={i} className="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t" style={{height: `${height}%`}}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Trusted by MSPs Worldwide</h2>
            <p className="text-lg text-slate-600">See what our customers have to say</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your MSP?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of MSPs already using our platform to grow their business
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')}
              className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8"
            >
              Start Free Trial
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/public/pricing')}
              className="border-white text-white hover:bg-white/10 text-lg px-8"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => navigate('/public/features')} className="hover:text-white">Features</button></li>
                <li><button onClick={() => navigate('/public/pricing')} className="hover:text-white">Pricing</button></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => navigate('/public/about')} className="hover:text-white">About Us</button></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => navigate('/public/resources')} className="hover:text-white">Help Center</button></li>
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white">Privacy</a></li>
                <li><a href="#" className="hover:text-white">Terms</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400">
            <p>&copy; 2025 RMM Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <VideoModal 
        isOpen={showVideoModal} 
        onClose={() => setShowVideoModal(false)} 
      />
    </div>
  );
};

export default LandingPage;